import React, { useEffect, useState } from "react";
import { getAllUsers } from "./userService";
import { Link } from "react-router-dom";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [resetStatus, setResetStatus] = useState({});

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    const data = await getAllUsers();
    setUsers(data);
  };

  const handlePasswordReset = async (user) => {
    const emailToUse = user.email || user.auth_email;
    if (!emailToUse) {
      alert("No email found for this user. Cannot send reset email.");
      return;
    }
    const auth = getAuth();
    setResetStatus((prev) => ({ ...prev, [user.id]: "sending" }));
    try {
      await sendPasswordResetEmail(auth, emailToUse);
      setResetStatus((prev) => ({ ...prev, [user.id]: "sent" }));
      setTimeout(() => setResetStatus((prev) => ({ ...prev, [user.id]: null })), 3000);
    } catch (err) {
      console.error("Password reset error:", err);
      setResetStatus((prev) => ({ ...prev, [user.id]: "error" }));
      setTimeout(() => setResetStatus((prev) => ({ ...prev, [user.id]: null })), 3000);
    }
  };

  const filteredUsers = users.filter((user) =>
    user.name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase()) ||
    user.phone?.toLowerCase().includes(search.toLowerCase()) ||
    user.role?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Management</h1>
        <Link to="/users/add" className="bg-blue-600 text-white px-4 py-2 rounded">
          Add User
        </Link>
      </div>

      <input
        type="text"
        placeholder="Search users..."
        className="border p-2 rounded w-full mb-4"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="bg-white shadow rounded overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Email / Phone</th>
              <th className="p-3 text-left">Role</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className="border-t">
                <td className="p-3 font-medium">{user.name}</td>
                <td className="p-3 text-gray-600">
                  {user.role === "technician"
                    ? (user.phone ? `📱 ${user.phone}` : "—")
                    : (user.email || "—")}
                </td>
                <td className="p-3 capitalize">{user.role}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    user.active === true || user.status === "active"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-600"
                  }`}>
                    {user.active === true || user.status === "active" ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <Link to={`/users/edit/${user.id}`} className="text-blue-600 hover:underline text-sm">
                      Edit
                    </Link>
                    {/* Only show Reset Password for non-technician users */}
                    {user.role !== "technician" && (
                      <button
                        onClick={() => handlePasswordReset(user)}
                        disabled={resetStatus[user.id] === "sending"}
                        className={`text-sm px-2 py-1 rounded border transition-all ${
                          resetStatus[user.id] === "sent"
                            ? "border-green-500 text-green-600 bg-green-50"
                            : resetStatus[user.id] === "error"
                            ? "border-red-400 text-red-600 bg-red-50"
                            : resetStatus[user.id] === "sending"
                            ? "border-gray-300 text-gray-400 cursor-not-allowed"
                            : "border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600"
                        }`}
                      >
                        {resetStatus[user.id] === "sending" ? "Sending..."
                          : resetStatus[user.id] === "sent" ? "✓ Sent"
                          : resetStatus[user.id] === "error" ? "✗ Failed"
                          : "Reset Password"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
